import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { api, ApiError } from '@/lib/api';

interface SearchHit {
  id: string;
  email: string | null;
  username: string | null;
}

interface Props {
  keepId: string;
  keepEmail: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onMerged?: () => void;
}

export function MergeUsersModal({ keepId, keepEmail, open, onOpenChange, onMerged }: Props) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<{ id: string; label: string }[]>([]);
  const [stage, setStage] = useState<'pick' | 'confirm'>('pick');
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['admin', 'users-merge-search', search],
    queryFn: () =>
      api.get<{ data: SearchHit[] }>(
        `/api/admin/users?q=${encodeURIComponent(search)}&limit=10`,
      ),
    enabled: search.length >= 2,
  });

  const merge = useMutation({
    mutationFn: () =>
      api.post<{ data: { deleted: string[]; reassigned: Record<string, number> } }>(
        '/api/admin/users/merge',
        { keepId, mergeIds: picked.map((p) => p.id), confirm: 'MERGE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'user', keepId] });
      onOpenChange(false);
      setStage('pick');
      setPicked([]);
      setSearch('');
      onMerged?.();
    },
  });

  function addToMerge(u: SearchHit) {
    if (u.id === keepId) return;
    if (picked.some((p) => p.id === u.id)) return;
    setPicked([...picked, { id: u.id, label: u.email ?? u.username ?? u.id.slice(0, 8) }]);
    setSearch('');
  }

  function handleOpenChange(v: boolean) {
    onOpenChange(v);
    if (!v) {
      setStage('pick');
      setPicked([]);
      setSearch('');
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Слить пользователей в этого</DialogTitle>
        </DialogHeader>
        {stage === 'pick' ? (
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">
              Оставляем: <strong>{keepEmail ?? keepId.slice(0, 8)}</strong>
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Найти юзера по email / username…"
            />
            {data?.data && search.length >= 2 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {data.data
                  .filter((u) => u.id !== keepId && !picked.some((p) => p.id === u.id))
                  .map((u) => (
                    <button
                      key={u.id}
                      onClick={() => addToMerge(u)}
                      className="block w-full text-left px-3 py-2 rounded hover:bg-zinc-800 text-sm"
                    >
                      {u.email ?? u.username ?? u.id}
                    </button>
                  ))}
              </div>
            )}
            {picked.length > 0 && (
              <div className="border-t border-zinc-800 pt-3 space-y-1">
                <div className="text-xs text-zinc-500">К объединению:</div>
                {picked.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span>{p.label}</span>
                    <button
                      onClick={() => setPicked(picked.filter((x) => x.id !== p.id))}
                      className="text-zinc-500 text-xs"
                    >
                      убрать
                    </button>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button disabled={picked.length === 0} onClick={() => setStage('confirm')}>
                Далее
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="text-zinc-300">
              Будут слиты в <strong>{keepEmail ?? keepId.slice(0, 8)}</strong>:
            </div>
            <ul className="list-disc pl-5 text-zinc-400">
              {picked.map((p) => <li key={p.id}>{p.label}</li>)}
            </ul>
            <div className="text-red-300 text-xs">Это нельзя отменить.</div>
            {merge.isError && (
              <div className="text-red-300 text-xs">
                {merge.error instanceof ApiError ? merge.error.message : 'Ошибка слияния'}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStage('pick')}>Назад</Button>
              <Button
                variant="destructive"
                disabled={merge.isPending}
                onClick={() => merge.mutate()}
              >
                {merge.isPending ? '...' : 'Слить'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
